import { Provider } from "@/modules/dashboard/cards/types/organizations-api.types";
import type {
  AdjustWalletBalancePayload,
  AdjustWalletBalanceResponse,
  WalletTransactionEntity,
} from "@/modules/dashboard/cards/types/wallets-api.types";
import type { WalletEntity } from "@/shared/types/wallet.entity";
import {
  addDecimals,
  compareDecimalStrings,
  isDecimalString,
  isPositiveDecimal,
  subtractDecimals,
  trimTrailingZeros,
} from "@/shared/utils/decimal";

import type { AdjustDirection, CardProgram, CardWalletIneligibility } from "../types/cardWallets.types";

/** `@MaxLength(120)` on `AdjustWalletBalanceDto.external_reference`. */
export const ADJUST_REFERENCE_MAX_LENGTH = 120;

/** The currency the DTO pins with `@IsIn(['USD'])`, and that the wallet's own currency must equal. */
export const ADJUST_CURRENCY = "USD";

/** USD settles at 2 dp, and the ledger column is `numeric(15,2)` — more decimals are silently rounded. */
export const ADJUST_AMOUNT_DECIMALS = 2;

/**
 * The backend derives the movement's `provider_transaction_id` from the reference
 * (`cm-adjust:<walletId>:<external_reference>`), so this prefix is the one reliable way to tell a manual
 * adjustment apart from a provider top-up: both are written with `transaction_type: "TOPUP"`.
 */
export const MANUAL_ADJUSTMENT_PREFIX = "cm-adjust:";

export function adjustProviderTransactionId(walletId: string, reference: string): string {
  return `${MANUAL_ADJUSTMENT_PREFIX}${walletId}:${reference}`;
}

export function isManualAdjustment(movement: Pick<WalletTransactionEntity, "provider_transaction_id">) {
  return (movement.provider_transaction_id ?? "").startsWith(MANUAL_ADJUSTMENT_PREFIX);
}

/** Last 8 characters of the uuid — what the wallet is called on screen, never the whole id. */
export function shortWalletId(walletId: string): string {
  return walletId.slice(-8);
}

/** Coloca-branded name of a card program. Rule 12: the vendor's own name never reaches the screen. */
export function cardProgramLabelKey(
  provider: CardProgram,
): "program_crypto" | "program_cards" | "program_none" {
  if (provider === Provider.CRYPTO_MATE) return "program_crypto";
  if (provider === Provider.BLOQUE) return "program_cards";
  return "program_none";
}

/**
 * Whether the organization can be served at all, decided from its `card_provider` ALONE.
 *
 * Returning a verdict here is what lets the page skip the wallet read for everyone else: that read provisions
 * a wallet, and for a Coloca Cards organization provisioning reaches the external provider.
 */
export function ineligibilityFromProgram(provider: CardProgram): CardWalletIneligibility | null {
  if (provider === Provider.CRYPTO_MATE) return null;
  if (provider === Provider.BLOQUE) return "not_crypto";
  return "no_program";
}

/** The remaining two conditions, which only the wallet itself can answer. */
export function ineligibilityFromWallet(wallet: WalletEntity): CardWalletIneligibility | null {
  if (String(wallet.wallet_type).toUpperCase() !== "CARD") return "not_card_wallet";
  if (String(wallet.provider?.provider ?? "").toUpperCase() !== Provider.CRYPTO_MATE) return "not_crypto";
  if (String(wallet.currency).toUpperCase() !== ADJUST_CURRENCY) return "not_usd";
  return null;
}

export type AdjustAmountProblem = "empty" | "not_positive" | "precision" | "over_balance";

/**
 * What is wrong with the typed magnitude, if anything. The magnitude is always UNSIGNED — the sign comes from
 * `direction`, so `-` is never typed and never parsed here.
 *
 * `balance` is only consulted for a debit, and only when it is readable: the backend rejects an overdraft
 * with a generic English sentence that mentions a card top-up, so the client is the only place that can say
 * what actually went wrong.
 */
export function adjustAmountProblem(
  magnitude: string,
  direction: AdjustDirection,
  balance: string | null,
): AdjustAmountProblem | null {
  const value = magnitude.trim();
  if (!value) return "empty";
  if (!isDecimalString(value) || !isPositiveDecimal(value)) return "not_positive";
  const decimals = value.split(".")[1]?.length ?? 0;
  if (decimals > ADJUST_AMOUNT_DECIMALS) return "precision";
  if (direction === "debit" && balance !== null && isDecimalString(balance)) {
    if (compareDecimalStrings(value, balance) > 0) return "over_balance";
  }
  return null;
}

export type AdjustReferenceProblem = "empty" | "too_long";

export function adjustReferenceProblem(reference: string): AdjustReferenceProblem | null {
  const value = reference.trim();
  if (!value) return "empty";
  if (value.length > ADJUST_REFERENCE_MAX_LENGTH) return "too_long";
  return null;
}

/** Balance once the movement is applied, in exact decimal-string arithmetic. */
export function balanceAfterAdjustment(
  balance: string,
  magnitude: string,
  direction: AdjustDirection,
): string | null {
  if (!isDecimalString(balance) || !isDecimalString(magnitude)) return null;
  const next =
    direction === "credit" ? addDecimals(balance, magnitude) : subtractDecimals(balance, magnitude);
  return trimTrailingZeros(next);
}

/** The movement as the review and the receipt show it: signed, so `−` is rendered by the display component. */
export function signedMovementValue(magnitude: string, direction: AdjustDirection): string {
  if (!isDecimalString(magnitude)) return magnitude;
  return direction === "credit" ? magnitude : `-${magnitude}`;
}

/**
 * The wire body. `amount` must be a real `number` (`@IsNumber()` rejects a string), so this is the one place
 * that leaves the decimal-string domain — guarded both ways: finite, and a lossless round-trip back to the
 * typed string at 2 dp. A paste with more significant digits than a double can hold is refused here rather
 * than silently rounded by Postgres.
 */
export function buildAdjustPayload(
  magnitude: string,
  direction: AdjustDirection,
  reference: string,
): AdjustWalletBalancePayload | null {
  if (adjustAmountProblem(magnitude, direction, null) !== null) return null;
  if (adjustReferenceProblem(reference) !== null) return null;
  const parsed = Number(magnitude);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (compareDecimalStrings(parsed.toFixed(ADJUST_AMOUNT_DECIMALS), magnitude) !== 0) return null;
  return {
    amount: direction === "credit" ? parsed : -parsed,
    currency: ADJUST_CURRENCY,
    external_reference: reference.trim(),
  };
}

/**
 * The response's three numbers become decimal strings right here, so nothing downstream ever holds a number
 * next to a string. Safe at USD 2 dp, and stated as a rule so nobody later feeds the raw number to
 * `ColocaAmountDisplay` beside a balance that is a string.
 */
export function normalizeAdjustResponse(response: AdjustWalletBalanceResponse) {
  return {
    walletId: response.walletId,
    previousBalance: response.previousBalance.toFixed(ADJUST_AMOUNT_DECIMALS),
    newBalance: response.newBalance.toFixed(ADJUST_AMOUNT_DECIMALS),
  };
}
